import { Component, h, Prop, State, Method, Element, Watch } from '@stencil/core';
import { getClassListString } from '../component-utils/stencilUtils';

enum TransitionStatus {
  COLLAPSED = 'COLLAPSED',
  COLLAPSING = 'COLLAPSING',
  EXPANDED = 'EXPANDED',
  EXPANDING = 'EXPANDING',
}

/**
 * COMPNAME is for creating a container for author info at the bottom of a journal article.
 */
@Component({
  tag: 'aafp-journal-article-author-info',
  shadow: false,
})
export class AafpJournalArticleAuthorInfo {
  /**
   * For the heading
   */
  @Prop() heading!: string;

  /**
   * For the author(s) - richtext
   */
  @Prop() authorInfo: string;

  /**
   * For the disclosure - rich text
   */
  @Prop() authorDisclosures: string;

  /**
   * Should the component disable truncation even if there are multiple paragraphs of text content?
   */
  @Prop() forceTruncationDisabled: boolean;

  @State() transitionStatus: TransitionStatus = TransitionStatus.COLLAPSED;
  @State() truncationDisabled: boolean = false;

  @Element() element: HTMLElement;

  private contentElement: HTMLElement;
  private currentRaf: number;

  componentWillLoad() {
    if (this.forceTruncationDisabled) {
      this.onTruncationDisabled();
    } else {
      this.maybeDisableTruncation(this.authorInfo, this.authorDisclosures);
    }
  }

  disconnectedCallback() {
    window.cancelAnimationFrame(this.currentRaf);
  }

  @Watch('authorInfo')
  parseAuthorInfoProp(newValue: string) {
    this.maybeDisableTruncation(newValue, this.authorDisclosures);
  }

  @Watch('authorDisclosures')
  parseAuthorDisclosuresProp(newValue: string) {
    this.maybeDisableTruncation(this.authorInfo, newValue);
  }

  @Watch('forceTruncationDisabled')
  onForceTruncationDisabledChange(newValue: boolean) {
    if (newValue) {
      this.onTruncationDisabled();
    }
  }

  @Method()
  async toggle(shouldAnimate?: boolean) {
    if (this.transitionStatus === TransitionStatus.EXPANDING || this.transitionStatus === TransitionStatus.EXPANDED) {
      this.collapse(shouldAnimate);
    } else {
      this.expand(shouldAnimate);
    }
  }

  @Method()
  async expand(shouldAnimate?: boolean) {
    if (shouldAnimate) {
      if (this.transitionStatus !== TransitionStatus.COLLAPSED) {
        return;
      }

      window.cancelAnimationFrame(this.currentRaf);
      this.animateExpand();
    } else {
      window.cancelAnimationFrame(this.currentRaf);
      this.contentElement.style.height = '';
      this.transitionStatus = TransitionStatus.EXPANDED;
    }
  }

  @Method()
  async collapse(shouldAnimate?: boolean) {
    if (shouldAnimate) {
      if (this.transitionStatus !== TransitionStatus.EXPANDED) {
        return;
      }

      window.cancelAnimationFrame(this.currentRaf);
      this.animateCollapse();
    } else {
      window.cancelAnimationFrame(this.currentRaf);
      this.contentElement.style.height = '';
      this.transitionStatus = TransitionStatus.COLLAPSED;
    }
  }

  private animateExpand() {
    // measure the current height of the content
    const currentHeight = parseInt(window.getComputedStyle(this.contentElement).getPropertyValue('height'), 10);

    if (currentHeight === 0) {
      this.transitionStatus = TransitionStatus.EXPANDED;
      return;
    }

    // set the current height as inline style
    this.contentElement.style.height = `${currentHeight}px`;

    // update state to expanding, applies css classes
    this.transitionStatus = TransitionStatus.EXPANDING;

    // wait a tick
    this.currentRaf = window.requestAnimationFrame(() => {
      // measure the height the content would be when fully expanded
      const scrollHeight = this.contentElement.scrollHeight;

      // define a callback for when the component is completely expanded
      const onTransitionEnd = (e?: Event) => {
        if (e && e.target !== this.contentElement) {
          return;
        }

        this.contentElement.removeEventListener('transitionend', onTransitionEnd, false);

        if (this.transitionStatus !== TransitionStatus.EXPANDING) {
          return;
        }

        // update state to expanded, clear out inline style for the height
        this.transitionStatus = TransitionStatus.EXPANDED;
        this.contentElement.style.height = '';
      };

      if (currentHeight >= scrollHeight) {
        onTransitionEnd();
        return;
      }

      // set the height to its final value to kick off the css transition
      this.contentElement.addEventListener('transitionend', onTransitionEnd, false);
      this.contentElement.style.height = `${scrollHeight}px`;
    });
  }

  private animateCollapse() {
    // measure the current height of the content
    const currentHeight = parseInt(window.getComputedStyle(this.contentElement).getPropertyValue('height'), 10);

    // set the current height as inline style
    this.contentElement.style.height = `${currentHeight}px`;

    // update state to collapsing, applies css classes
    this.transitionStatus = TransitionStatus.COLLAPSING;

    // wait a tick
    this.currentRaf = window.requestAnimationFrame(() => {
      // define a callback for when the component is completely collapsed
      const onTransitionEnd = (e?: Event) => {
        if (e && e.target !== this.contentElement) {
          return;
        }

        this.contentElement.removeEventListener('transitionend', onTransitionEnd, false);

        if (this.transitionStatus !== TransitionStatus.COLLAPSING) {
          return;
        }

        // update state to collapsed, clear out inline style for the height
        this.transitionStatus = TransitionStatus.COLLAPSED;
        this.contentElement.style.height = '';
      };

      // set height to only the height of the first item to kick off the css transition
      let targetHeight = 0;

      const firstChild: Element | null = this.contentElement.firstElementChild;

      if (firstChild) {
        targetHeight = firstChild.scrollHeight;
      }

      if (currentHeight <= targetHeight) {
        onTransitionEnd();
        return;
      }

      this.contentElement.addEventListener('transitionend', onTransitionEnd, false);
      this.contentElement.style.height = `${targetHeight}px`;
    });
  }

  private maybeDisableTruncation(authorInfo: string, authorDisclosures: string) {
    const tmpEl = document.createElement('div');
    tmpEl.innerHTML = (authorInfo || '') + (authorDisclosures || '');

    const childCount = tmpEl.childElementCount;

    if (childCount <= 1) {
      this.truncationDisabled = true;
      this.onTruncationDisabled();
    } else {
      this.truncationDisabled = false;
    }
  }

  private onTruncationDisabled() {
    window.cancelAnimationFrame(this.currentRaf);

    if (this.contentElement) {
      this.contentElement.style.height = '';
    }

    this.transitionStatus = TransitionStatus.EXPANDED;
  }

  render() {
    const disableTruncation = this.truncationDisabled || this.forceTruncationDisabled;

    return (
      <div
        class={getClassListString(
          {
            'adl-journal-article-author-info--expanded': this.transitionStatus === TransitionStatus.EXPANDED,
            'adl-journal-article-author-info--expanding': this.transitionStatus === TransitionStatus.EXPANDING,
            'adl-journal-article-author-info--collapsed': this.transitionStatus === TransitionStatus.COLLAPSED,
            'adl-journal-article-author-info--collapsing': this.transitionStatus === TransitionStatus.COLLAPSING,
          },
          'adl-journal-article-author-info',
        )}
      >
        <h2 class="adl-journal-article-author-info__heading" id="aafp-journal-article-author-info__heading">
          {disableTruncation ? (
            this.heading
          ) : (
            <button
              class="adl-journal-article-author-info__toggle-btn"
              onClick={() => this.toggle(true)}
              aria-controls="aafp-journal-article-author-info__list"
              aria-expanded={String(
                this.transitionStatus === TransitionStatus.EXPANDED ||
                  this.transitionStatus === TransitionStatus.EXPANDING,
              )}
            >
              <span class="material-icons" aria-hidden="true">
                expand_more
              </span>
              {this.heading}
            </button>
          )}
        </h2>
        <div
          class="adl-journal-article-author-info__content adl-body"
          id="aafp-journal-article-refernces__content"
          aria-labelledby="aafp-journal-article-author-info__heading"
          ref={(el) => (this.contentElement = el as HTMLElement)}
          innerHTML={`${this.authorInfo || ''}${this.authorDisclosures || ''}`}
        />
      </div>
    );
  }
}
